import React from "react";
import {
  DatePickerField,
  DropdownField,
  TagsDropdownField,
  TextInputField,
} from "../../../../../../components/inputs";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../../store/config";
import { useEffect, useState } from "react";
import {
  AbsoluteButton55,
  RelativeFlex,
  StyledForm,
  StyledLayout,
  StyledVerticalDivider,
} from "../../../../../../components/common/styles";
import { useLocation, useParams } from "react-router-dom";
import { Divider, Flex, Form, notification, Spin } from "antd";
import dayjs from "dayjs";
import { useInterestsTags } from "../../../../../trainers/useSelectOptions";
import { StyledHalfWidthFlex, StyledWrapper } from "./style";
import { updateUserStart } from "../../../../../../store/features/user/user-slice";
import { ForgotPasswordModal } from "../../../../../../components/forgot-password/ForgotPassword";

export const UserPersonalDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();

  const { isEdit } = location.state || {};

  const { id: userId } = useParams<{ id: string }>();
  const [isEditable, setIsEditable] = useState(false);

  const { user, loading, error, requestState, operation } = useAppSelector(
    (state) => state.user
  );

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        gender: user.gender,
        dob: dayjs(user.dob),
        registeredAt: dayjs(user.registeredAt),
        lastSeen: user.lastSeen ? dayjs(user.lastSeen) : null,
        subscribed: user.subscribed ? t("Active") : t("Inactive"),
        height: user.fitnessProfile?.height,
        weight: user.fitnessProfile?.initialWeight,
        trainerGenderPreference: user.fitnessProfile?.trainerGenderPreference,
        trainerRegistrationCode: user.trainerRegistrationCode || "",
        interests: user.fitnessProfile?.interests || [],
        registeredVia:
          user.registeredVia === "password"
            ? t("email")
            : t(user.registeredVia),
        isRegisteredWithTrainerCode: user?.trainerRegistrationCode
          ? t("Yes")
          : t("No"),
      });
    }
  }, [user]);

  useEffect(() => {
    if (isEdit) setIsEditable(true);
  }, [isEdit]);

  useEffect(() => {
    if (requestState === "finish" && operation === "update") {
      setIsEditable(false);
    }
  }, [requestState, operation, user, userId]);

  useEffect(() => {
    if (error) openNotification();
  }, [error]);

  const updateUser = (values: any) => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }
    if (!userId) return;

    const userBody = {
      email: values.email,
      gender: values.gender || null,
    };

    const fitnessProfileBody = {
      height: values.height || null,
      initialWeight: values.weight || null,
      trainerGenderPreference: values.trainerGenderPreference || null,
      interests: values.interests,
    };

    const updateUserBody = {
      user: { account: userBody, fitnessProfile: fitnessProfileBody },
      userId: userId,
    };

    dispatch(updateUserStart(updateUserBody));
  };

  const openNotification = (error?: string) => {
    api["error"]({
      message: t(error || "Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  return (
    <>
      {contextHolder}

      <RelativeFlex>
        <AbsoluteButton55 onClick={() => form.submit()}>
          {isEditable ? t("Save") : t("Edit")}
        </AbsoluteButton55>
      </RelativeFlex>

      <Spin spinning={loading}>
        <StyledForm
          form={form}
          onFinish={updateUser}
          validateTrigger="onSubmit"
        >
          <StyledLayout>
            <StyledWrapper>
              <StyledHalfWidthFlex>
                <Flex gap={20}>
                  <TextInputField
                    name="firstName"
                    label={t("First Name")}
                    isEditable={false}
                  />
                  <TextInputField
                    name="lastName"
                    label={t("Last Name")}
                    isEditable={false}
                  />
                </Flex>

                <Flex gap={20}>
                  <TextInputField
                    name="email"
                    label={t("Email")}
                    isEditable={isEditable}
                    type="email"
                    rules={[
                      { required: true, message: t("Required") },
                      {
                        type: "email",
                        message: t("Invalid email address"),
                      },
                    ]}
                  />
                </Flex>

                <Flex gap={20}>
                  <DropdownField
                    label={t("Gender")}
                    options={[
                      { label: t("Male"), value: "MALE" },
                      { label: t("Female"), value: "FEMALE" },
                    ]}
                    isEditable={isEditable}
                    name="gender"
                  />
                  <DatePickerField
                    label={t("Date of Birth")}
                    isEditable={false}
                    name="dob"
                  />
                </Flex>
                <Divider style={{ margin: "10px" }} />

                <Flex gap={20}>
                  <DatePickerField
                    label={t("Registration Date")}
                    isEditable={false}
                    name="registeredAt"
                  />
                  <DatePickerField
                    label={t("Last Seen")}
                    isEditable={false}
                    name="lastSeen"
                  />
                </Flex>

                <Flex gap={20}>
                  <TextInputField
                    name="subscribed"
                    label={t("Subscription Status")}
                    isEditable={false}
                  />

                  {/* <DatePickerField
                    label={t("Trial Period")}
                    isEditable={false}
                    name="dob"
                    maxDate={dayjs()}
                  /> */}
                </Flex>

                {user?.registeredVia === "password" && (
                  <ForgotPasswordModal
                    openNotification={openNotification}
                    email={user?.email}
                  />
                )}
              </StyledHalfWidthFlex>

              <StyledVerticalDivider />

              <StyledHalfWidthFlex>
                <Flex gap={20}>
                  <TextInputField
                    name="height"
                    label={t("Height")}
                    isEditable={isEditable}
                  />
                  <TextInputField
                    name="weight"
                    label={t("Weight")}
                    isEditable={isEditable}
                  />
                </Flex>

                <Flex gap={20}>
                  <DropdownField
                    name="trainerGenderPreference"
                    label={t("Trainer Gender Preference")}
                    options={[
                      { label: t("Female Trainer"), value: "FEMALE" },
                      { label: t("Male Trainer"), value: "MALE" },
                      { label: t("No Preference"), value: "OTHER" },
                    ]}
                    isEditable={isEditable}
                  />

                  <TextInputField
                    name="registeredVia"
                    label={t("Registered Via")}
                    isEditable={false}
                  />
                </Flex>

                <Flex gap={20}>
                  <TextInputField
                    label={t("Registered with Trainer Code")}
                    name="isRegisteredWithTrainerCode"
                    isEditable={false}
                  />

                  <TextInputField
                    name="trainerRegistrationCode"
                    label={t("Trainer Registration Code")}
                    isEditable={false}
                  />
                </Flex>

                <TagsDropdownField
                  mode="multiple"
                  label={t("Interests")}
                  options={useInterestsTags()}
                  name="interests"
                  isEditable={isEditable}
                />
              </StyledHalfWidthFlex>
            </StyledWrapper>
          </StyledLayout>
        </StyledForm>
      </Spin>
    </>
  );
};
